<template>
  <div id="scholarship-bulk-upload">
    <overlay-loading-progress :LoadingFlag="SubmitFlag" />

    <!-- Header Card -->
    <v-card elevation="20" class="header-card mb-6 overflow-hidden">
      <div class="header-gradient">
        <div class="header-backdrop-effect"></div>
        <v-container fluid class="header-container">
          <v-row align="center" class="fill-height pa-4">
            <v-col sm="7" xs="12" class="header-content d-flex align-center">
              <div class="header-icon-box">
                <v-icon size="48" color="white">mdi-cloud-upload</v-icon>
              </div>
              <div class="header-title-bar">
                <h1 class="header-title">{{ PageTitle }}</h1>
                <p class="header-subtitle">{{ YearName }}</p>
              </div>
            </v-col>
            <v-col sm="5" xs="12" class="d-flex justify-end align-center gap-3">
              <v-btn
                @click.prevent="refreshPageData"
                icon
                rounded
                x-large
                class="action-btn"
              >
                <v-icon>mdi-refresh</v-icon>
              </v-btn>
              <v-btn
                @click.prevent="backPage"
                icon
                rounded
                x-large
                class="action-btn"
              >
                <v-icon>mdi-arrow-left</v-icon>
              </v-btn>
            </v-col>
          </v-row>
        </v-container>
      </div>
    </v-card>

    <!-- Description Card -->
    <v-card elevation="4" class="mb-6 info-card">
      <v-card-text class="pa-4">
        <v-row align="center">
          <v-col cols="auto">
            <v-icon size="32" color="primary">mdi-information-outline</v-icon>
          </v-col>
          <v-col>
            <h4 class="text-subtitle-1 font-weight-600">
              {{ PageDescription }}
            </h4>
            <p class="text-body2 mb-0">
              Upload scholarship issued data using the provided template
            </p>
          </v-col>
        </v-row>
      </v-card-text>
    </v-card>

    <v-container fluid class="pa-0">
      <v-row>
        <!-- Sample Template Card -->
        <v-col cols="12" md="12">
          <v-card elevation="4" class="sample-card mb-6">
            <v-card-text class="pa-0">
              <v-row align="center" class="fill-height">
                <v-col cols="12" sm="6" class="pa-6">
                  <div class="d-flex align-center">
                    <div class="template-icon-box">
                      <v-icon size="48" color="white">mdi-file-excel</v-icon>
                    </div>
                    <div class="ml-6">
                      <h3 class="text-h6 font-weight-700 mb-1">
                        Download Template
                      </h3>
                      <p class="text-body2 mb-0">
                        Get the required Excel format
                      </p>
                    </div>
                  </div>
                </v-col>
                <v-col
                  cols="12"
                  sm="6"
                  class="pa-6 d-flex justify-end align-center"
                >
                  <a
                    :href="SampleFilePath"
                    :download="SampleFilePath"
                    class="download-btn"
                  >
                    <v-icon left small>mdi-download</v-icon>
                    Download Template
                  </a>
                </v-col>
              </v-row>
            </v-card-text>
          </v-card>
        </v-col>

        <!-- Upload Form Card -->
        <v-col cols="12" md="6">
          <v-card elevation="4" class="form-card">
            <v-card-title class="card-header card-header--upload">
              <v-icon class="mr-3">mdi-upload-box</v-icon>
              <span>Upload File</span>
            </v-card-title>
            <v-card-text class="pa-6">
              <v-form
                ref="form1"
                v-model="valid1"
                lazy-validation
                @submit.prevent="submitForm"
              >
                <div class="form-field">
                  <label class="field-label required-field"
                    >Select Excel File</label
                  >
                  <v-file-input
                    v-model="UploadedFile1"
                    accept=".xls,.xlsx"
                    placeholder="Choose Excel file"
                    prepend-icon="mdi-file-excel"
                    truncate-length="25"
                    :clearable="true"
                    :rules="fileRules"
                    outlined
                    dense
                    class="field-input"
                  >
                    <template v-slot:append>
                      <v-icon
                        v-if="UploadedFile1.length > 0"
                        color="error"
                        @click="UploadedFile1 = []"
                        class="cursor-pointer"
                      >
                        mdi-close-circle
                      </v-icon>
                    </template>
                  </v-file-input>
                </div>

                <v-row class="mt-6 d-flex justify-center">
                  <v-col cols="12" sm="6" md="5" class="d-flex justify-center">
                    <v-btn
                      type="submit"
                      class="submit-btn font-weight-700"
                      :loading="SubmitFlag"
                    >
                      <v-icon left>mdi-check-circle</v-icon>
                      Submit Upload
                    </v-btn>
                  </v-col>
                </v-row>
              </v-form>
            </v-card-text>
          </v-card>
        </v-col>

        <!-- Upload Instructions Card -->
        <v-col cols="12" md="6">
          <v-card elevation="4" class="instructions-card">
            <v-card-title class="card-header card-header--info">
              <v-icon class="mr-3">mdi-alert-circle</v-icon>
              <span>Upload Instructions</span>
            </v-card-title>
            <v-card-text class="pa-6">
              <div class="instruction-item">
                <div class="d-flex gap-4 align-start">
                  <v-icon size="28" color="success" class="mt-1 flex-shrink-0"
                    >mdi-check-circle</v-icon
                  >
                  <div class="flex-grow-1">
                    <h4 class="text-body1 font-weight-700 mb-2">
                      Excel Format Required
                    </h4>
                    <p class="text-body2 mb-0">
                      Upload only .xlsx, .CSV or .xls files
                    </p>
                  </div>
                </div>
              </div>
              <div class="instruction-item">
                <div class="d-flex gap-4 align-start">
                  <v-icon size="28" color="success" class="mt-1 flex-shrink-0"
                    >mdi-check-circle</v-icon
                  >
                  <div class="flex-grow-1">
                    <h4 class="text-body1 font-weight-700 mb-2">
                      Follow Template Format
                    </h4>
                    <p class="text-body2 mb-0">
                      Use the provided template for consistency
                    </p>
                  </div>
                </div>
              </div>
              <div class="instruction-item">
                <div class="d-flex gap-4 align-start">
                  <v-icon size="28" color="success" class="mt-1 flex-shrink-0"
                    >mdi-check-circle</v-icon
                  >
                  <div class="flex-grow-1">
                    <h4 class="text-body1 font-weight-700 mb-2">
                      Valid Data Only
                    </h4>
                    <p class="text-body2 mb-0">
                      Ensure all required fields are filled correctly
                    </p>
                  </div>
                </div>
              </div>
              <div class="instruction-item last-item">
                <div class="d-flex gap-4 align-start">
                  <v-icon size="28" color="success" class="mt-1 flex-shrink-0"
                    >mdi-check-circle</v-icon
                  >
                  <div class="flex-grow-1">
                    <h4 class="text-body1 font-weight-700 mb-2">
                      No Duplicates
                    </h4>
                    <p class="text-body2 mb-0">
                      Remove any duplicate records before uploading
                    </p>
                  </div>
                </div>
              </div>
            </v-card-text>
          </v-card>
        </v-col>
      </v-row>
      <br />
      <!-- Results Table -->
      <v-col cols="12" v-if="rows.length > 0" class="pa-0">
        <v-card elevation="4" class="table-card">
          <v-card-title class="card-header card-header--results">
            <v-icon class="mr-3">mdi-table</v-icon>
            <span>Upload Results ({{ rows.length }} Records)</span>
          </v-card-title>
          <v-card-text class="pa-0">
            <div class="table-wrapper">
              <v-data-table
                v-model="selected"
                class="professional-table"
                :headers="TableColumns1"
                :items="rows"
                :items-per-page="50"
                :search="search"
                :single-select="false"
                :show-select="false"
                :footer-props="{
                  'items-per-page-options': [10, 20, 30, 40, 50],
                }"
              >
                <template v-slot:top>
                  <v-toolbar flat class="table-toolbar">
                    <v-text-field
                      v-model="search"
                      append-icon="mdi-magnify"
                      label="Search results..."
                      single-line
                      hide-details
                      class="table-search"
                    ></v-text-field>
                  </v-toolbar>
                </template>
              </v-data-table>
            </div>
          </v-card-text>
        </v-card>
      </v-col>
    </v-container>
  </div>
</template>


<script>
import common from "@/view/Common.vue";
import companyConfig from "@/company_config.json";
import StudentScholarshipPreview from "@/view/pages/erp/student-scholarship/StudentScholarshipPreview.vue";
import Swal from "sweetalert2";

export default {
  components: { StudentScholarshipPreview },

  mixins: [common],
  computed: {
    SampleFilePath() {
      return (
        companyConfig.apiURL + "assets/sample/scholarship_upload_template.xlsx"
      );
    },
  },
  data() {
    return {
      valid1: true,
      valid2: true,
      LoadingFlag: false,
      SearchFlag: false,
      SearchButtonFlag: false,
      SubmitFlag: false,
      SpeedDialFlag: false,
      search: "",

      DownloadFlag: 0,
      ExcelFileName: "",
      ExcelFields: "",
      BulkUploadPermissionFlag: "",
      recordToEdit: "",
      record: {},
      addRecordPrompt: false,
      editRecordPrompt: false,
      previewRecordPrompt: false,
      editPageDescription: "Assign LOM President for JCI India",

      LomDataPageTitle: "",
      LomDataPreviewFlag: false,
      LomDataParams: {},

      rows: [],
      selected: [],
      TableColumns1: [],
      TableOptions1: {
        CreateButtonFlag: false,
      },

      selectedData: {},
      UploadedFile1: [],
      direction: "top",
      fab: true,
      fling: false,
      hover: true,
      tabs: null,
      top: false,
      right: true,
      bottom: true,
      left: false,
      transition: "slide-y-reverse-transition",

      fileRules: [(v) => !!v || "File is required"],

      zone: {},
      lom: {},

      tableData1: {},

      PageInfo: {},
      PageTitle: "",
      PageDescription: "",
      CurrentYearId: 0,
      JciYearCode: "",
      YearName: "",
      NextYearCode: "",
      NextYearName: "",
    };
  },
  mounted() {
    /*
          this.$store.dispatch(SET_BREADCRUMB, [
            { title: "Vuetify", route: "alerts" },
            { title: "Form Inputs & Control", route: "autocompletes" },
            { title: "Fileinptus" },
          ]);
          */
  },
  watch: {
    PageInfo: function () {
      console.log("watch PageInfo");
      this.LoadingFlag = false;
      var tr = this.PageInfo;
      console.log({ tr });
      var n1 = Object.keys(tr).length;
      console.log({ n1 });
      if (n1 > 0) {
        var flag = tr.PagePermissionFlag;
        console.log("PagePermissionFlag=" + flag);

        if (!flag) {
          this.$router.push("/access-denied");
        }
        this.PageTitle = tr.PageTitle;
        this.PageDescription = tr.PageDescription;

        var CurrentJciYearId = parseInt(tr.CurrentJciYearId);
        console.log({ CurrentJciYearId });
        this.JciYearCode = CurrentJciYearId;

        var YearName = parseInt(tr.CurrentJciYearName);
        console.log({ YearName });
        this.YearName = YearName;

        var NextJciYearId = parseInt(tr.NextJciYearId);
        console.log({ NextJciYearId });
        this.NextYearCode = NextJciYearId;

        var NextJciYearName = parseInt(tr.NextJciYearName);
        console.log({ NextJciYearName });
        this.NextYearName = NextJciYearName;

        this.CurrentYearId = CurrentJciYearId;

        this.pageData();
      }
    },
  },
  methods: {
    refreshPageData() {
      console.log("refreshPageData called");
      this.LoadingFlag = true;
      var url1 = "api/menu-details/show";
      var condition1 = {
        UserInterface: 1,
        Module: "student_scholarship",
        Action: "approval",
      };
      this.getPageDetails("PageInfo", {}, url1, condition1);
    },
    backPage() {
      this.$router.go(-1);
    },
    pageData() {
      console.log("pageData called");
      this.getJciYearCodeOptions();
    },
    submitForm() {
      console.log("submitForm is called");

      var validate1 = this.$refs.form1.validate();
      console.log({ validate1 });

      if (validate1) {
        this.TableOptions1 = {};
        this.TableColumns1 = [];
        this.rows = [];
        this.SubmitFlag = true;

        var server_url = companyConfig.apiURL;
        var token = this.$session.get("token");
        token = token == (null || undefined) ? 0 : token;
        var add_url = server_url + "api/student/scholarship-issued/bulk-upload";

        let upload = new FormData();
        upload.append("UserInterface", 1);
        upload.append("UploadedFile", this.UploadedFile1);

        console.log("upload=" + JSON.stringify(upload));
        console.log(
          "server_url=" +
            server_url +
            ", token=" +
            token +
            ", add_url=" +
            add_url
        );

        const thisIns = this;
        var output = "";
        var records = {};
        var flag = 0;

        this.$http({
          url: add_url,
          method: "POST",
          data: upload,
          headers: {
            "Content-Type": "application/json",
            Authorization: "Bearer " + token,
          },
          onUploadProgress: function (progressEvent) {
            this.uploadPercentage = parseInt(
              Math.round((progressEvent.loaded / progressEvent.total) * 100)
            );
          }.bind(this),
        })
          .then(function (response) {
            console.log({ response });

            thisIns.SubmitFlag = false;

            flag = response.data.flag;
            output = response.data.output;
            console.log({ flag }, { output });

            if (flag == 1) {
              thisIns.toast("success", output);
              // thisIns.$router.push(HomePage).catch(() => true);
            } else {
              thisIns.toast("error", output);
            }

            records = response.data.records;
            console.log({ records });

            thisIns.TableOptions1 = records.TableOptions;
            thisIns.TableColumns1 = records.TableHeader;
            thisIns.rows = records.TableData;
          })
          .catch(function (error) {
            console.log("error=" + error);
            thisIns.SubmitFlag = false;
          });
      } else {
        var message = "Kindly fill all required fields";
        this.toast("error", message);
      }
    },
  },
  beforeMount() {
    this.refreshPageData();
  },
};
</script>
<style lang="scss">
// Color Palette - JCI Brand Colors
$navy: #3a67b1;
$aqua: #0097d7;
$gold: #edbe38;
$seaform: #56bda3;
$text-90-black: #414042;
$text-off-black: #0a0f29;
$text-white: #ffffff;
$success-color: #4caf50;
$warning-color: #ff9800;
$error-color: #f44336;

// Animations
@keyframes slideDown {
  from {
    opacity: 0;
    transform: translateY(-20px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

@keyframes slideInUp {
  from {
    opacity: 0;
    transform: translateY(30px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

@keyframes slideInLeft {
  from {
    opacity: 0;
    transform: translateX(-30px);
  }
  to {
    opacity: 1;
    transform: translateX(0);
  }
}

@keyframes float {
  0%,
  100% {
    transform: translateY(0px);
  }
  50% {
    transform: translateY(-12px);
  }
}

@keyframes shimmer {
  0% {
    transform: translateX(-100%);
  }
  100% {
    transform: translateX(100%);
  }
}

#scholarship-bulk-upload {
  background: linear-gradient(135deg, #f5f7fa 0%, #c3cfe2 100%);
  min-height: 100vh;
  padding: 30px 15px;

  // ============================================
  // HEADER CARD
  // ============================================
  .header-card {
    background: linear-gradient(135deg, $navy 0%, $aqua 100%) !important;
    border-radius: 20px;
    overflow: hidden;
    animation: slideDown 0.8s cubic-bezier(0.34, 1.56, 0.64, 1);
    position: relative;
    border: none;
    box-shadow: 0 20px 60px rgba(58, 103, 177, 0.5),
      0 0 30px rgba(0, 151, 215, 0.3);

    &::before {
      content: "";
      position: absolute;
      top: -50%;
      right: -50%;
      width: 200%;
      height: 200%;
      background: radial-gradient(
        circle,
        rgba(255, 255, 255, 0.1) 0%,
        transparent 70%
      );
      animation: float 8s ease-in-out infinite;
    }
  }

  .header-backdrop-effect {
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%23ffffff' fill-opacity='0.05'%3E%3Cpath d='M36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6 34v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6 4V0H4v4H0v2h4v4h2V6h4V4H6z'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E");
    opacity: 0.5;
  }

  .header-container {
    padding: 20px 0 !important;
  }

  .header-icon-box {
    background: linear-gradient(
      135deg,
      rgba(255, 255, 255, 0.25) 0%,
      rgba(255, 255, 255, 0.15) 100%
    );
    backdrop-filter: blur(15px);
    border-radius: 18px;
    padding: 14px;
    box-shadow: 0 12px 40px rgba(0, 0, 0, 0.2),
      inset 0 1px 0 rgba(255, 255, 255, 0.4);
    animation: float 4s ease-in-out infinite;
    border: 2px solid rgba(255, 255, 255, 0.3);
    display: flex;
    align-items: center;
    justify-content: center;
    flex-shrink: 0;
    margin-right: 20px;
  }

  .header-title-bar {
    display: flex;
    flex-direction: column;
    gap: 4px;
  }

  .header-title {
    font-size: 36px;
    font-weight: 900;
    text-shadow: 2px 2px 8px rgba(0, 0, 0, 0.3), 0 0 20px rgba(0, 151, 215, 0.3);
    animation: slideInLeft 0.8s ease-out;
    letter-spacing: 0.8px;
    color: $text-white;
    margin: 0;
  }

  .header-subtitle {
    font-size: 14px;
    opacity: 0.95;
    animation: slideInLeft 1s ease-out 0.1s backwards;
    letter-spacing: 0.3px;
    color: rgba($text-white, 0.9);
    font-weight: 500;
    margin: 0;
  }

  .action-btn {
    background: rgba(255, 255, 255, 0.15) !important;
    border: 2px solid rgba(255, 255, 255, 0.3) !important;
    backdrop-filter: blur(10px);
    color: white !important;
    transition: all 0.4s cubic-bezier(0.34, 1.56, 0.64, 1);

    &:hover {
      transform: translateY(-6px);
      background: rgba(255, 255, 255, 0.25) !important;
      border-color: rgba(255, 255, 255, 0.5) !important;
      box-shadow: 0 12px 32px rgba(0, 0, 0, 0.3) !important;
    }
  }

  .gap-3 {
    gap: 1rem !important;
  }

  // ============================================
  // INFO CARD
  // ============================================
  .info-card {
    border-radius: 16px !important;
    border: 2px solid rgba(58, 103, 177, 0.15) !important;
    box-shadow: 0 12px 40px rgba(58, 103, 177, 0.12),
      0 0 30px rgba(0, 151, 215, 0.08) !important;
    background: linear-gradient(135deg, #ffffff 0%, #f8fafc 100%) !important;
    animation: slideInUp 0.6s ease-out;
    transition: all 0.3s ease;

    &:hover {
      transform: translateY(-4px);
      box-shadow: 0 16px 50px rgba(58, 103, 177, 0.15),
        0 0 40px rgba(0, 151, 215, 0.12) !important;
    }
  }

  // ============================================
  // SAMPLE TEMPLATE CARD
  // ============================================
  .sample-card {
    border-radius: 16px !important;
    background: linear-gradient(
      135deg,
      rgba(0, 151, 215, 0.08) 0%,
      rgba(58, 103, 177, 0.08) 100%
    ) !important;
    border: 2px solid rgba(0, 151, 215, 0.2) !important;
    box-shadow: 0 8px 24px rgba(0, 151, 215, 0.12) !important;
    animation: slideInUp 0.6s ease-out 0.1s backwards;
    transition: all 0.3s ease;
    position: relative;
    overflow: hidden;

    &::before {
      content: "";
      position: absolute;
      top: -50%;
      right: -50%;
      width: 300px;
      height: 300px;
      background: radial-gradient(
        circle,
        rgba(0, 151, 215, 0.15) 0%,
        transparent 70%
      );
      animation: float 6s ease-in-out infinite;
      pointer-events: none;
    }

    &:hover {
      transform: translateY(-4px);
      box-shadow: 0 12px 32px rgba(0, 151, 215, 0.18) !important;
    }
  }

  .template-icon-box {
    background: linear-gradient(135deg, $navy 0%, $aqua 100%);
    border-radius: 14px;
    padding: 12px;
    display: flex;
    align-items: center;
    justify-content: center;
    box-shadow: 0 8px 20px rgba(0, 151, 215, 0.25);
    flex-shrink: 0;
  }

  .download-btn {
    display: inline-flex;
    align-items: center;
    gap: 8px;
    padding: 12px 28px;
    background: linear-gradient(135deg, $navy 0%, $aqua 100%);
    color: white;
    text-decoration: none;
    border-radius: 12px;
    font-weight: 700;
    font-size: 13px;
    letter-spacing: 0.5px;
    box-shadow: 0 8px 24px rgba(0, 151, 215, 0.25);
    transition: all 0.4s cubic-bezier(0.34, 1.56, 0.64, 1);
    position: relative;
    overflow: hidden;
    cursor: pointer;

    &::before {
      content: "";
      position: absolute;
      top: 0;
      left: -100%;
      width: 100%;
      height: 100%;
      background: linear-gradient(
        90deg,
        transparent,
        rgba(255, 255, 255, 0.3),
        transparent
      );
      animation: shimmer 2s infinite;
    }

    &:hover {
      transform: translateY(-4px);
      box-shadow: 0 12px 32px rgba(0, 151, 215, 0.35);
    }
  }

  .submit-btn {
    background: linear-gradient(135deg, $navy 0%, $aqua 100%) !important;
    color: white !important;
    padding: 14px 40px !important;
    border-radius: 12px !important;
    font-size: 14px !important;
    font-weight: 700 !important;
    letter-spacing: 0.5px !important;
    box-shadow: 0 8px 24px rgba(0, 151, 215, 0.25) !important;
    transition: all 0.4s cubic-bezier(0.34, 1.56, 0.64, 1) !important;
    position: relative;
    overflow: hidden;
    display: inline-flex !important;
    align-items: center !important;

    &::before {
      content: "";
      position: absolute;
      top: 0;
      left: -100%;
      width: 100%;
      height: 100%;
      background: linear-gradient(
        90deg,
        transparent,
        rgba(255, 255, 255, 0.3),
        transparent
      );
      animation: shimmer 2s infinite;
    }

    &:hover:not(:disabled) {
      transform: translateY(-4px) !important;
      box-shadow: 0 12px 32px rgba(0, 151, 215, 0.35) !important;
    }

    &::v-deep {
      .v-btn__content {
        position: relative;
        z-index: 1;
        display: flex;
        align-items: center;
        gap: 8px;
      }
    }
  }

  // ============================================
  // FORM CARD
  // ============================================
  .form-card {
    border-radius: 16px !important;
    background: linear-gradient(135deg, #ffffff 0%, #f8fafc 100%) !important;
    box-shadow: 0 12px 32px rgba(58, 103, 177, 0.12) !important;
    border: 2px solid rgba(0, 151, 215, 0.1) !important;
    animation: slideInUp 0.6s ease-out 0.15s backwards;
    transition: all 0.3s ease;

    .card-header {
      display: flex;
      align-items: center;
      gap: 12px;
      padding: 18px;
      border-radius: 14px 14px 0 0;
      background: linear-gradient(135deg, $navy 0%, $aqua 100%);
      position: relative;
      overflow: hidden;
      z-index: 1;

      &::before {
        content: "";
        position: absolute;
        top: -50%;
        right: -50%;
        width: 200%;
        height: 200%;
        background: radial-gradient(
          circle,
          rgba(255, 255, 255, 0.1) 0%,
          transparent 70%
        );
        animation: float 6s ease-in-out infinite;
      }

      span {
        color: white;
        font-weight: 700;
        font-size: 16px;
        position: relative;
        z-index: 2;
      }
    }

    .card-header--upload {
      background: linear-gradient(135deg, #0097d7 0%, #0078b4 100%);
    }

    .card-header--info {
      background: linear-gradient(135deg, #56bda3 0%, #3a9b7e 100%);
    }

    .card-header--results {
      background: linear-gradient(135deg, #edbe38 0%, #d4a823 100%);
    }
  }

  .form-field {
    margin-bottom: 20px;

    .field-label {
      font-size: 12px;
      font-weight: 700;
      text-transform: uppercase;
      letter-spacing: 0.5px;
      color: $navy;
      display: block;
      margin-bottom: 8px;

      &.required-field::after {
        content: "*";
        color: $error-color;
        margin-left: 4px;
      }
    }

    .field-input {
      transition: all 0.3s ease;

      &::v-deep {
        .v-input__control .v-input__slot {
          border-color: rgba(0, 151, 215, 0.2) !important;
        }

        &.v-input--is-focused {
          .v-input__slot {
            border-color: $aqua !important;
            box-shadow: 0 0 0 2px rgba(0, 151, 215, 0.1);
          }
        }
      }
    }
  }

  // ============================================
  // INSTRUCTIONS CARD
  // ============================================
  .instructions-card {
    border-radius: 16px !important;
    background: linear-gradient(135deg, #f0f9ff 0%, #f8fafc 100%) !important;
    box-shadow: 0 12px 32px rgba(58, 103, 177, 0.12) !important;
    border: 2px solid rgba(86, 189, 163, 0.2) !important;
    animation: slideInUp 0.6s ease-out 0.2s backwards;
    transition: all 0.3s ease;

    .instruction-item {
      padding: 20px 0;
      border-bottom: 2px solid rgba(86, 189, 163, 0.15);
      transition: all 0.3s ease;

      &:hover {
        background: rgba(86, 189, 163, 0.08);
        padding: 20px 12px;
        border-radius: 8px;
        margin: 0 -12px;
        padding-left: 20px;
      }

      &.last-item {
        border-bottom: none;
      }

      h4 {
        color: $navy;
        margin: 0;
        transition: all 0.3s ease;
      }

      p {
        margin: 0;
        color: $text-90-black;
        line-height: 1.5;
      }
    }
  }

  // ============================================
  // TABLE CARD
  // ============================================
  .table-card {
    border-radius: 16px !important;
    background: #ffffff !important;
    box-shadow: 0 12px 32px rgba(58, 103, 177, 0.12) !important;
    border: 2px solid rgba(0, 151, 215, 0.1) !important;
    animation: slideInUp 0.6s ease-out 0.25s backwards;
    overflow: hidden;

    .table-wrapper {
      overflow-x: auto;
    }

    .table-toolbar {
      background: linear-gradient(
        135deg,
        rgba(58, 103, 177, 0.05) 0%,
        rgba(0, 151, 215, 0.05) 100%
      ) !important;
      border-bottom: 2px solid rgba(0, 151, 215, 0.1);
      padding: 12px 16px !important;

      .table-search {
        max-width: 300px;

        &::v-deep {
          .v-input__control .v-input__slot {
            background: white !important;
            border: 2px solid rgba(0, 151, 215, 0.2) !important;
            border-radius: 8px;
          }
        }
      }
    }
  }

  .professional-table {
    &::v-deep {
      thead tr {
        background: linear-gradient(
          135deg,
          rgba(58, 103, 177, 0.08) 0%,
          rgba(0, 151, 215, 0.08) 100%
        ) !important;

        th {
          font-weight: 700 !important;
          color: $navy !important;
          font-size: 12px !important;
          text-transform: uppercase !important;
          letter-spacing: 0.5px !important;
          border-bottom: 2px solid rgba(0, 151, 215, 0.15) !important;
          padding: 16px 12px !important;
        }
      }

      tbody tr {
        transition: all 0.3s ease;
        border-bottom: 1px solid rgba(0, 0, 0, 0.05) !important;

        &:hover {
          background: linear-gradient(
            135deg,
            rgba(0, 151, 215, 0.05) 0%,
            rgba(58, 103, 177, 0.05) 100%
          ) !important;
          box-shadow: inset 0 0 10px rgba(0, 151, 215, 0.08);
        }

        td {
          padding: 14px 12px !important;
          color: $text-90-black;
          font-size: 13px;
        }
      }

      .v-data-table__expanded__content {
        background: linear-gradient(
          135deg,
          rgba(0, 151, 215, 0.03) 0%,
          rgba(58, 103, 177, 0.03) 100%
        ) !important;
      }
    }
  }

  // ============================================
  // RESPONSIVE DESIGN
  // ============================================
  @media (max-width: 960px) {
    padding: 20px 12px;

    .header-card {
      margin-bottom: 20px !important;
    }

    .header-title {
      font-size: 28px !important;
    }

    .header-subtitle {
      font-size: 13px !important;
    }
  }

  @media (max-width: 600px) {
    padding: 15px;

    .header-card {
      margin-bottom: 15px !important;
    }

    .header-title {
      font-size: 24px !important;
    }

    .header-subtitle {
      font-size: 12px !important;
    }

    .sample-card {
      margin-bottom: 15px !important;

      &::v-deep .v-card__text {
        .v-row {
          flex-direction: column;

          .v-col {
            width: 100% !important;
          }
        }
      }
    }

    .download-btn {
      width: 100%;
      justify-content: center;
    }
  }
}
</style>